import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import styles from './index.module.scss'
import sparkIcon_long from '../../assets/images/share/sparkIcon_hr.png'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { Button } from '@mui/material'
import { TabNames } from '../../routers/type'
import Header from '../../components/Header'
import Checkbox from '@mui/material/Checkbox'
import ReCAPTCHA from 'react-google-recaptcha'
import {
    getAuth,
    createUserWithEmailAndPassword,
    setPersistence,
    browserSessionPersistence,
} from 'firebase/auth'
import { getDatabase, ref, set } from 'firebase/database'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
// import { setFirstTime } from "../../redux/userRegistrationSlice";
import { Modal } from '@mui/material'
import pxToRem from '../../helpers/pxToRem'
import { LoginSx } from '../../components/TextField'
import { setErrorMsg, setRegisterAs } from '../../redux/uiSlice'
import z from 'zod'
import { zodPassword } from '../../schemas/helper'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInputText from '../../components/FormComponents/FormInputText'

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0px 4px 4px 0px #00000040',
    borderRadius: pxToRem(16),
    background: '#fff',
    // overflow: 'scroll',
}

const formSchema = z
    .object({
        firstName: z.string().min(2, 'At least 2 characters required'),
        lastName: z.string().min(2, 'At least 2 characters required'),
        email: z.email(),
        password: zodPassword,
        confirmPassword: z.string(),
        termsAndConditions: z.boolean().refine((v) => v),
    })
    .check((ctx) => {
        if (ctx.value.password !== ctx.value.confirmPassword) {
            ctx.issues.push({
                code: 'custom',
                message: 'Passwords do not match',
                path: ['confirmPassword'],
                input: ctx.value,
            })
        }
    })
type FormType = z.infer<typeof formSchema>

const SignupPage = () => {
    const {
        control,
        handleSubmit,
        formState: { isValid, isSubmitting },
    } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            termsAndConditions: false,
        },
        mode: 'onChange',
    })

    const [showPassword, setShowPassword] = useState(false)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [showCaptcha, openCaptcha] = useState(false)
    // const [errMsg, setErrMsg] = useState("");
    const screenType = useSelector((state: RootState) => state.ui.screenType)

    const recaptchaRef = useRef<ReCAPTCHA>(null)

    const handleClickShowPassword = () => setShowPassword((show) => !show)

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const reCaptchaChange = (value: string | null) => {
        openCaptcha(false)
        recaptchaRef.current?.reset()
        if (!value) {
            dispatch(setErrorMsg('ReCaptcha expired or not activated'))
            return
        }
        handleSubmit(onSubmit)()
    }

    const onSubmit = async (data: FormType) => {
        try {
            await setPersistence(getAuth(), browserSessionPersistence)
            const userCredential = await createUserWithEmailAndPassword(
                getAuth(),
                data.email,
                data.password
            )
            const db = getDatabase()
            const currentDate = moment().format('DD-MM-YYYY')
            // @ts-ignore
            window.gtag('config', 'AW-10883425818')
            // @ts-ignore
            window.gtag('event', 'conversion', {
                send_to: 'AW-10883425818/AtYACOuO07gDEJrMz8Uo',
            })

            await set(ref(db, `users/${userCredential.user.uid}`), {
                dateRegistered: currentDate,
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                registrationDevice: 'web',
            })

            // NOTE: Email verification is temporarily disabled.
            navigate(`/${TabNames.DASHBOARD}`)
        } catch (error: any) {
            dispatch(
                setErrorMsg(
                    'We were unable to create an account using these credentials at this time.'
                )
            )
        }
    }

    useEffect(() => {
        return () => {
            dispatch(setRegisterAs(undefined))
        }
    }, [dispatch])

    return (
        <>
            {
                <>
                    <Modal open={showCaptcha} onClose={() => {}} keepMounted>
                        <Stack style={modalStyle}>
                            <ReCAPTCHA
                                className={styles.recapcha}
                                sitekey={
                                    process.env.REACT_APP_RECAPTCHA as string
                                }
                                onChange={reCaptchaChange}
                                ref={recaptchaRef}
                            />
                        </Stack>
                    </Modal>
                    <Box className={styles.header_wrapper}>
                        <Header />
                    </Box>
                    <Stack
                        className={`${styles.signupPage} ${
                            screenType === 'MOBILE' ? 'space_for_header' : ''
                        }`}
                    >
                        <Stack height={'100%'} className={styles.container}>
                            <Stack className={styles.leftContainer}>
                                <img
                                    src={sparkIcon_long}
                                    alt="sparkIcon_long"
                                    className={styles.spark_longIcon}
                                    onClick={() => navigate('/')}
                                />
                                <form>
                                    <Stack className={styles.inputStack}>
                                        <h1 id="formtitle">
                                            Register An Account
                                        </h1>
                                        <FormInputText
                                            name="firstName"
                                            label="First Name*"
                                            control={control}
                                            sx={LoginSx}
                                            autoComplete={'given-name'}
                                            slotProps={{
                                                input: {
                                                    autoComplete: 'given-name',
                                                },
                                                htmlInput: {
                                                    autoComplete: 'given-name',
                                                },
                                            }}
                                        />
                                        <FormInputText
                                            name="lastName"
                                            label="Last Name*"
                                            control={control}
                                            sx={LoginSx}
                                            autoComplete={'family-name'}
                                            slotProps={{
                                                input: {
                                                    autoComplete: 'family-name',
                                                },
                                                htmlInput: {
                                                    autoComplete: 'family-name',
                                                },
                                            }}
                                        />
                                        <FormInputText
                                            name="email"
                                            label="Email*"
                                            control={control}
                                            sx={LoginSx}
                                            autoComplete={'email'}
                                            slotProps={{
                                                input: {
                                                    autoComplete: 'email',
                                                },
                                                inputLabel: {
                                                    shrink: true,
                                                },
                                                htmlInput: {
                                                    autoComplete: 'email',
                                                },
                                            }}
                                        />
                                        <FormInputText
                                            name="password"
                                            label="Password*"
                                            control={control}
                                            sx={LoginSx}
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            slotProps={{
                                                inputLabel: { shrink: true },
                                                input: {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={
                                                                    handleClickShowPassword
                                                                }
                                                                onMouseDown={
                                                                    handleMouseDownPassword
                                                                }
                                                                edge="end"
                                                            >
                                                                {showPassword ? (
                                                                    <VisibilityOff />
                                                                ) : (
                                                                    <Visibility />
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                    autoComplete:
                                                        'new-password',
                                                },
                                                htmlInput: {
                                                    autoComplete:
                                                        'new-password',
                                                },
                                            }}
                                        />
                                        <FormInputText
                                            name="confirmPassword"
                                            label="Confirm Password"
                                            control={control}
                                            sx={LoginSx}
                                            type={'password'}
                                            autoComplete={'new-password'}
                                            slotProps={{
                                                input: {
                                                    autoComplete:
                                                        'new-password',
                                                },
                                                inputLabel: { shrink: true },
                                                htmlInput: {
                                                    autoComplete:
                                                        'new-password',
                                                },
                                            }}
                                        />
                                        <Stack
                                            direction={'row'}
                                            justifyContent="flex-start"
                                            alignItems={'center'}
                                        >
                                            <Controller
                                                name="termsAndConditions"
                                                control={control}
                                                render={(renderProps) => (
                                                    <Checkbox
                                                        name="termsAndConditions"
                                                        checked={
                                                            renderProps.field
                                                                .value as boolean
                                                        }
                                                        onChange={(e) =>
                                                            renderProps.field.onChange(
                                                                e.target.checked
                                                            )
                                                        }
                                                        icon={
                                                            // prettier-ignore
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3" />{' '} </svg>
                                                        }
                                                        checkedIcon={
                                                            // prettier-ignore
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_36_152)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#0081A3" />{' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#0081A3" />{' '} <path d="M18.6668 7L9.50016 16.1667L5.3335 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_36_152"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                                        }
                                                    />
                                                )}
                                            />
                                            <div
                                                className={`${styles.policiesStack}`}
                                            >
                                                <p>
                                                    I have read and agree to{' '}
                                                    <span
                                                        className={
                                                            styles.policyButton
                                                        }
                                                        role="link"
                                                        id="terms-of-service"
                                                        onClick={() =>
                                                            window.open(
                                                                `/${TabNames.TERMS_OF_SERVICE}`
                                                            )
                                                        }
                                                    >
                                                        Spark's Terms of Service
                                                    </span>{' '}
                                                    including the Privacy Policy
                                                </p>
                                            </div>
                                        </Stack>

                                        <Button
                                            className={styles.submitButton}
                                            loading={isSubmitting}
                                            onClick={() => {
                                                if (
                                                    !process.env
                                                        .REACT_APP_DISABLE_RECAPTCHA_FOR_E2E
                                                ) {
                                                    openCaptcha(true)
                                                } else {
                                                    handleSubmit(onSubmit)()
                                                }
                                            }}
                                            variant="contained"
                                            disabled={!isValid}
                                            name="registerSubmit"
                                        >
                                            {isSubmitting ? '' : 'Register'}
                                        </Button>
                                        <Stack
                                            className={`${styles.hasAccountStack}`}
                                        >
                                            <small>
                                                Already have an account?
                                            </small>
                                            <p
                                                role="link"
                                                id="login-link"
                                                onClick={() =>
                                                    navigate(
                                                        `/${TabNames.LOGIN}`
                                                    )
                                                }
                                            >
                                                Log in
                                            </p>
                                        </Stack>
                                    </Stack>
                                </form>
                            </Stack>
                            <Stack
                                className={`${styles.rightContainer} ${styles['rightContainer_bg--teacher']}`}
                            >
                                {/* prettier-ignore */}
                                <svg width="675" height="703" viewBox="0 0 675 703" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path opacity="0.3" d="M330.383 683.906L4.43295 9.03034C-7.07305 -14.7927 10.3157 -43.5923 36.1877 -43.5625L1565.54 -41.8029C1578.15 -41.7884 1589.52 -34.531 1595.13 -22.9164L1919.25 648.166C1930.73 671.939 1913.44 700.691 1887.62 700.759L1066.98 702.909L360.011 702.792C347.386 702.79 335.998 695.531 330.383 683.906Z" fill="#DFF4FF" />{' '} </svg>
                                <div className={styles.verticalLine}></div>
                                <Stack>
                                    <h1>
                                        Hello, Welcome to
                                        <br />
                                        Spark Relief Teachers
                                    </h1>
                                    <p>Simplifying the CRT booking process</p>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </>
            }
        </>
    )
}

export default SignupPage
